import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Users, Scissors, ChevronRight, ChevronLeft,
  Check, Phone, Mail, User, MessageSquare, ArrowLeft
} from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============= STEP 1: SERVICE SELECTION =============
const ServiceSelection = ({ services, selectedService, onSelect }) => {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-2">{t('bkChooseService')}</h2>
        <p className="text-gray-500">{t('bkAllOffers')}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service) => {
          const active = selectedService?.id === service.id;
          return (
            <button
              key={service.id}
              type="button"
              onClick={() => onSelect(service)}
              className={`text-left p-5 border-2 rounded-2xl transition-all group relative ${
                active
                  ? 'border-primary bg-primary/5 shadow-lg scale-[1.02]'
                  : 'border-gray-200 hover:border-primary/50 hover:shadow-md bg-white'
              }`}
              data-testid={`service-option-${service.id}`}
            >
              {active && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-primary/10 group-hover:text-primary'
                }`}>
                  <Scissors className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg leading-tight pr-8">{service.name}</h3>
                  {service.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{service.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-3.5 h-3.5" />
                      {service.duration_minutes} {t('bkMin')}
                    </span>
                    <span className={`text-xl font-bold ${active ? 'text-primary' : 'text-gray-900'}`}>
                      {(service.price_gross ?? service.price ?? 0).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============= STEP 2: STAFF SELECTION =============
const StaffSelection = ({ staff, selectedStaff, onSelect, onSkip }) => {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">{t('bkChooseStaff')}</h2>
      <div className="grid gap-3">
        <div
          onClick={onSkip}
          className={`p-4 border rounded-xl cursor-pointer transition-all ${
            selectedStaff === null 
              ? 'border-black bg-gray-50 ring-2 ring-black' 
              : 'hover:border-gray-400'
          }`}
          data-testid="no-staff-preference"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              <h3 className="font-medium">{t('bkNoPreference')}</h3>
              <p className="text-sm text-gray-500">{t('bkNextAvailable')}</p>
            </div>
            {selectedStaff === null && <Check className="w-5 h-5 text-green-500 ml-auto" />}
          </div>
        </div>
        
        {staff.map((member) => (
          <div
            key={member.id}
            onClick={() => onSelect(member)}
            className={`p-4 border rounded-xl cursor-pointer transition-all ${
              selectedStaff?.id === member.id 
                ? 'border-black bg-gray-50 ring-2 ring-black' 
                : 'hover:border-gray-400'
            }`}
            data-testid={`staff-option-${member.id}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                {member.image_url ? (
                  <img src={member.image_url} alt={member.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-6 h-6 text-gray-500" />
                )}
              </div>
              <div>
                <h3 className="font-medium">{member.name}</h3>
                {member.title && <p className="text-sm text-gray-500">{member.title}</p>}
              </div>
              {selectedStaff?.id === member.id && <Check className="w-5 h-5 text-green-500 ml-auto" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============= STEP 3: DATE & TIME SELECTION =============
const LOCALE_MAP = { de: 'de-DE', en: 'en-GB', tr: 'tr-TR', ar: 'ar-SA', it: 'it-IT', fr: 'fr-FR', es: 'es-ES' };

const DateTimeSelection = ({ shopSlug, serviceId, staffId, selectedDate, selectedTime, onSelectDate, onSelectTime }) => {
  const { t, lang } = useI18n();
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotInfo, setSlotInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Generate next 14 days
  const dates = [];
  const today = new Date();
  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (date) => {
    const locale = LOCALE_MAP[lang] || 'de-DE';
    return {
      weekday: date.toLocaleDateString(locale, { weekday: 'short' }),
      day: date.getDate(),
      month: date.toLocaleDateString(locale, { month: 'short' })
    };
  };

  useEffect(() => {
    if (selectedDate) {
      loadSlots(selectedDate);
    }
  }, [selectedDate, serviceId, staffId]);

  const loadSlots = async (date) => {
    setLoading(true);
    try {
      const staffParam = staffId ? `&staff_id=${staffId}` : '';
      const response = await fetch(
        `${API_URL}/api/public/shop/${shopSlug}/available-slots?date=${date}&service_id=${serviceId}${staffParam}`
      );
      const data = await response.json();
      setAvailableSlots(data.slots || []);
      setSlotInfo(data);
    } catch (error) {
      console.error('Failed to load slots', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">{t('bkChooseDateTime')}</h2>
      
      {/* Date Selection */}
      <div>
        <h3 className="font-medium mb-3">{t('date')}</h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {dates.map((date) => {
            const formatted = formatDate(date);
            const display = formatDateDisplay(date);
            const isSelected = selectedDate === formatted;
            const isToday = formatDate(today) === formatted;
            
            return (
              <button
                key={formatted}
                onClick={() => onSelectDate(formatted)}
                className={`flex-shrink-0 w-16 p-3 rounded-xl text-center transition-all ${
                  isSelected 
                    ? 'bg-black text-white' 
                    : 'bg-white border hover:border-gray-400'
                }`}
                data-testid={`date-${formatted}`}
              >
                <span className={`block text-xs ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                  {display.weekday}
                </span>
                <span className="block text-lg font-bold">{display.day}</span>
                <span className={`block text-xs ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                  {display.month}
                </span>
                {isToday && !isSelected && (
                  <span className="block text-xs text-blue-500 mt-1">{t('bkToday')}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <div>
          <h3 className="font-medium mb-3">
            {t('bkAvailableTimes')} {selectedDate && `(${new Date(selectedDate).toLocaleDateString(LOCALE_MAP[lang] || 'de-DE', { weekday: 'long' })})`}
          </h3>
          {loading ? (
            <div className="text-center py-8 text-gray-500">{t('loading')}</div>
          ) : availableSlots.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
              {t('bkNoSlots')}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {availableSlots.map((slot) => (
                <button
                  key={slot}
                  onClick={() => onSelectTime(slot)}
                  className={`py-3 px-2 rounded-lg text-center transition-all ${
                    selectedTime === slot 
                      ? 'bg-black text-white' 
                      : 'bg-white border hover:border-gray-400'
                  }`}
                  data-testid={`time-${slot}`}
                >
                  {slot}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============= STEP 4: CUSTOMER DETAILS =============
const CustomerDetails = ({ formData, onChange }) => {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">{t('bkYourContact')}</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            <User className="w-4 h-4 inline mr-2" />
            {t('name')} *
          </label>
          <input
            type="text"
            value={formData.customer_name}
            onChange={(e) => onChange({ ...formData, customer_name: e.target.value })}
            className="w-full border rounded-xl px-4 py-3"
            placeholder="Max Mustermann"
            required
            data-testid="customer-name-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            E-Mail *
          </label>
          <input
            type="email"
            value={formData.customer_email}
            onChange={(e) => onChange({ ...formData, customer_email: e.target.value })}
            className="w-full border rounded-xl px-4 py-3"
            placeholder="max@beispiel.de"
            required
            data-testid="customer-email-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            {t('phone')} *
          </label>
          <input
            type="tel"
            value={formData.customer_phone}
            onChange={(e) => onChange({ ...formData, customer_phone: e.target.value })}
            className="w-full border rounded-xl px-4 py-3"
            placeholder="+49 123 456789"
            required
            data-testid="customer-phone-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <MessageSquare className="w-4 h-4 inline mr-2" />
            {t('bkNotesOptional')}
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => onChange({ ...formData, notes: e.target.value })}
            className="w-full border rounded-xl px-4 py-3"
            rows={3}
            placeholder={t('bkNotesPlaceholder')}
            data-testid="customer-notes-input"
          />
        </div>
      </div>
    </div>
  );
};

// ============= STEP 5: CONFIRMATION =============
const BookingConfirmation = ({ booking, shop, shopSlug }) => {
  const { t } = useI18n();
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [saved, setSaved] = useState(false);
  const customerToken = localStorage.getItem(`b2c_token_${shopSlug}`) || localStorage.getItem('b2c_customer_token');
  const downloadIcal = () => {
    const url = `${API_URL}/api/public/shop/${shopSlug}/appointment/${booking.id}/ical`;
    window.open(url, '_blank');
  };
  
  const saveFavorite = async (silent = false) => {
    if (!customerToken || !booking.service_id) return;
    setSavingFavorite(true);
    try {
      const res = await fetch(`${API_URL}/api/public/shop/${shopSlug}/customer/favorites/${booking.service_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${customerToken}` },
      });
      if (!res.ok) throw new Error('save failed');
      setSaved(true);
      if (!silent) toast.success(t('bkSavedFavorite'));
    } catch (e) {
      if (!silent) toast.error('Konnte nicht speichern');
    } finally {
      setSavingFavorite(false);
    }
  };

  // Auto-save booked service as favorite for logged-in customers
  useEffect(() => {
    if (customerToken && booking?.service_id && !saved) {
      saveFavorite(true);
    }
  }, [customerToken, booking?.service_id]);

  return (
    <div className="text-center space-y-6" data-testid="booking-confirmation">
      <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
        <Check className="w-10 h-10 text-green-500" />
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-green-600">{t('bkBooked')}</h2>
        <p className="text-gray-600 mt-2">
          {t('bkConfirmationSent', { email: booking.customer_email })}
        </p>
      </div>
      
      <div className="bg-gray-50 rounded-xl p-6 text-left max-w-md mx-auto">
        <h3 className="font-bold mb-4">{t('bkDetails')}</h3>
        <div className="space-y-3">
          <div className="flex justify-between" data-testid="booking-number">
            <span className="text-gray-600">{t('bkBookingNumber')}</span>
            <span className="font-mono font-medium">#{(booking.id || '').slice(0, 8).toUpperCase()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('date')}:</span>
            <span className="font-medium">{booking.appointment_date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('time')}:</span>
            <span className="font-medium">{booking.appointment_time} {t('bkOClock')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('bkService')}</span>
            <span className="font-medium">{booking.service_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('bkDuration')}</span>
            <span className="font-medium">{booking.duration_minutes} {t('bkMinutes')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('price')}:</span>
            <span className="font-medium">{(booking.service_price ?? 0).toFixed(2)} €</span>
          </div>
          {booking.staff_name && (
            <div className="flex justify-between">
              <span className="text-gray-600">Mitarbeiter:</span>
              <span className="font-medium">{booking.staff_name}</span>
            </div>
          )}
        </div>
        
        {/* Calendar Download Button */}
        {customerToken && booking.service_id && (
          <button
            onClick={saveFavorite}
            disabled={savingFavorite || saved}
            className="w-full mt-4 py-3 px-4 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            data-testid="save-favorite-btn"
          >
            {saved ? '★ Als Favorit gespeichert' : t('bkSaveFavorite')}
          </button>
        )}
        <button
          onClick={downloadIcal}
          className="w-full mt-4 py-3 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
          data-testid="download-ical-btn"
        >
          <Calendar className="w-5 h-5" />
          <span>{t('bkAddToCalendar')}</span>
        </button>
      </div>
      
      <p className="text-sm text-gray-500">
        {t('bkQuestions')}
      </p>
    </div>
  );
};

// ============= MAIN BOOKING PAGE =============
const PublicBookingPage = () => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedServiceId = searchParams.get('service');
  
  const [shop, setShop] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customerData, setCustomerData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: ''
  });
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    loadShopData();
  }, [shopSlug]);

  const loadShopData = async () => {
    try {
      // Load shop info
      const shopRes = await fetch(`${API_URL}/api/public/shop/${shopSlug}`);
      if (!shopRes.ok) {
        navigate('/');
        return;
      }
      const shopData = await shopRes.json();
      setShop(shopData);
      
      // Load services
      const servicesRes = await fetch(`${API_URL}/api/public/shop/${shopSlug}/services`);
      const servicesData = await servicesRes.json();
      setServices(servicesData);
      
      // Auto-select service if URL parameter present (1-click rebooking from favorites)
      if (preselectedServiceId) {
        const preselected = servicesData.find(s => s.id === preselectedServiceId);
        if (preselected) {
          setSelectedService(preselected);
          setStep(2);
        }
      }
      
      // Load staff
      const staffRes = await fetch(`${API_URL}/api/public/shop/${shopSlug}/staff`);
      const staffData = await staffRes.json();
      setStaff(staffData);
      
    } catch (error) {
      console.error('Failed to load shop data', error);
      toast.error('Shop konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedDate || !selectedTime) {
      toast.error(t('bkSelectAll'));
      return;
    }
    if (!customerData.customer_name || !customerData.customer_email || !customerData.customer_phone) {
      toast.error(t('fillRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/shop/${shopSlug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: selectedService.id,
          staff_id: selectedStaff?.id || null,
          appointment_date: selectedDate,
          appointment_time: selectedTime,
          ...customerData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Buchung fehlgeschlagen');
      }

      const data = await response.json();
      setBooking(data.appointment);
      setStep(5);
      toast.success(t('bkBookedSuccess'));
    } catch (error) {
      toast.error(error.message || 'Buchung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return selectedService !== null;
      case 2: return true; // Staff is optional
      case 3: return selectedDate && selectedTime;
      case 4: return customerData.customer_name && customerData.customer_email && customerData.customer_phone;
      default: return false;
    }
  };

  const nextStep = () => {
    if (step === 2 && staff.length === 0) {
      // Skip staff selection if no staff
      setStep(3);
    } else if (step === 4) {
      handleSubmit();
    } else {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step === 3 && staff.length === 0) {
      setStep(1);
    } else {
      setStep(step - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Shop nicht gefunden</p>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate(`/shop/${shopSlug}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-black mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('psBackToShop')}
          </button>
          <div className="bg-white rounded-xl p-8 text-center">
            <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('bkNotAvailable')}</h2>
            <p className="text-gray-500">{t('bkNotOffered')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-booking-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => step > 1 && step < 5 ? prevStep() : navigate(`/shop/${shopSlug}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-black"
          >
            <ArrowLeft className="w-5 h-5" />
            {step > 1 && step < 5 ? t('back') : t('toShop')}
          </button>
          <div className="text-center">
            <h1 className="font-bold">{shop.shop_name}</h1>
            <p className="text-xs text-gray-500">{t('bkBookingTitle')}</p>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Progress Bar */}
      {step < 5 && (
        <div className="bg-white border-b">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-500">{t('bkStepOf', { step, total: 4 })}</span>
              <span className="text-gray-500">{Math.round((step / 4) * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-black transition-all duration-300"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          {step === 1 && (
            <ServiceSelection 
              services={services}
              selectedService={selectedService}
              onSelect={(service) => {
                setSelectedService(service);
                // Auto-advance to next step
                if (staff.length > 0) {
                  setStep(2);
                } else {
                  setStep(3);
                }
              }}
            />
          )}
          
          {step === 2 && (
            <StaffSelection 
              staff={staff}
              selectedStaff={selectedStaff}
              onSelect={(member) => {
                setSelectedStaff(member);
                setStep(3);
              }}
              onSkip={() => {
                setSelectedStaff(null);
                setStep(3);
              }}
            />
          )}
          
          {step === 3 && (
            <DateTimeSelection 
              shopSlug={shopSlug}
              serviceId={selectedService?.id}
              staffId={selectedStaff?.id}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectDate={setSelectedDate}
              onSelectTime={(time) => {
                setSelectedTime(time);
                setStep(4);
              }}
            />
          )}
          
          {step === 4 && (
            <CustomerDetails 
              formData={customerData}
              onChange={setCustomerData}
            />
          )}
          
          {step === 5 && booking && (
            <BookingConfirmation 
              booking={booking}
              shop={shop}
              shopSlug={shopSlug}
            />
          )}
        </div>

        {/* Summary Sidebar - visible on steps 2-4 */}
        {step > 1 && step < 5 && selectedService && (
          <div className="mt-6 bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium mb-3">{t('bkYourSelection')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('bkService')}</span>
                <span>{selectedService.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('bkDuration')}</span>
                <span>{selectedService.duration_minutes} {t('bkMin')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('price')}:</span>
                <span className="font-bold">{(selectedService.price_gross ?? selectedService.price ?? 0).toFixed(2)} €</span>
              </div>
              {selectedStaff && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Mitarbeiter:</span>
                  <span>{selectedStaff.name}</span>
                </div>
              )}
              {selectedDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('date')}:</span>
                  <span>{selectedDate}</span>
                </div>
              )}
              {selectedTime && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('time')}:</span>
                  <span>{selectedTime} {t('bkOClock')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {step === 4 && (
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className={`w-full py-4 rounded-xl font-medium text-white transition-all ${
                canProceed() && !submitting
                  ? 'bg-black hover:bg-gray-800' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="submit-booking-btn"
            >
              {submitting ? t('bkSubmitting') : t('bkSubmitBooking')}
            </button>
            <p className="text-xs text-gray-500 text-center mt-3">
              {t('bkAcceptTerms')}
            </p>
          </div>
        )}

        {step === 5 && (
          <div className="mt-6 space-y-3">
            <button
              onClick={() => navigate(`/shop/${shopSlug}`)}
              className="w-full py-4 rounded-xl font-medium bg-black text-white hover:bg-gray-800"
              data-testid="back-to-shop-btn"
            >
              {t('psBackToShop')}
            </button>
            <button
              onClick={() => {
                setStep(1);
                setSelectedService(null);
                setSelectedStaff(null);
                setSelectedDate(null);
                setSelectedTime(null);
                setCustomerData({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
                setBooking(null);
              }}
              className="w-full py-4 rounded-xl font-medium border border-black hover:bg-gray-50"
              data-testid="book-another-btn"
            >
              {t('bkAnotherAppointment')}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicBookingPage;
